// Conversation file attachments: fetch, preview and save — always through the
// membership-gated API, resolved fresh from the message id on every open.
//
// Why this exists (2026-10-22, "the PDF opened earlier, now it errors"): the
// card used to point a bare <a href>/<img src> at the file endpoint. Two things
// broke that silently:
//   1. the bytes themselves vanished on the server at each deploy (fixed in
//      conversation-service: durable storage + a 410 "no longer available");
//   2. a bare link can't renew an expired 15-minute session cookie and can't
//      show a sensible message — the person got a raw JSON error tab or a
//      broken image.
// Here every open is an authenticated request that renews the session ONCE on
// a 401, retries ONCE on a network failure, and maps each failure to words.
// Nothing is cached: no URL, signed or otherwise, outlives the click.
import i18n from '@/i18n/config';
import { getApiErrorMessage } from '@/lib/apiError';

export type PreviewKind = 'pdf' | 'image' | 'office' | 'none';

/** The nine Office formats conversation-service can turn into a PDF (Office
 *  preview spec §2). Macro-enabled names (.docm/.xlsm/.pptm) are not here: the
 *  server decides from the bytes anyway, and refuses macros. */
const OFFICE_EXT = ['docx', 'pptx', 'xlsx', 'odt', 'odp', 'ods', 'doc', 'xls', 'ppt'];
const OFFICE_MIME = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
]);

/** What can be shown in place: PDFs and images by the browser; Office files as a
 *  PDF the server prepares (View). Everything else (archives, text…) downloads. */
export function previewKind(mime?: string, name?: string): PreviewKind {
  const m = (mime || '').toLowerCase();
  const ext = (name || '').toLowerCase().split('.').pop() || '';
  if (m === 'application/pdf' || ext === 'pdf') return 'pdf';
  // SVG is excluded on purpose: it can carry script, and it's rare in chat.
  if ((m.startsWith('image/') && m !== 'image/svg+xml') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  if (OFFICE_MIME.has(m) || OFFICE_EXT.includes(ext)) return 'office';
  return 'none';
}

export type AttachmentFailure = 'gone' | 'forbidden' | 'not_found' | 'network' | 'server';

interface HttpLikeError {
  response?: { status?: number; data?: unknown };
  code?: string;
}

/** Classify a failed fetch. No response at all = the network, never "gone". */
export function classifyAttachmentError(e: unknown): AttachmentFailure {
  const status = (e as HttpLikeError)?.response?.status;
  if (!status) return 'network';
  if (status === 410) return 'gone';
  if (status === 401 || status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  return 'server';
}

/** Human message for a failed open. The server's own words win when it gave any. */
export function attachmentErrorMessage(e: unknown): string {
  const t = (k: string, d: string) => i18n.t(`conversations:file.${k}`, { defaultValue: d }) as string;
  if ((e as { code?: string })?.code === 'preview_timeout') {
    return i18n.t('conversations:preview.timeout', {
      defaultValue: 'The preview is taking too long. Try again in a moment, or download the file.',
    }) as string;
  }
  switch (classifyAttachmentError(e)) {
    case 'gone':
      return getApiErrorMessage(e, t('gone', 'This file is no longer available. Ask the sender to share it again.'));
    case 'forbidden':
      return t('forbidden', 'Your session has ended or you no longer have access to this file. Sign in again and retry.');
    case 'not_found':
      return t('not_found', 'This file was removed or you are no longer in this conversation.');
    case 'network':
      return t('network', 'Couldn’t reach KaziPeople. Check your connection and try again.');
    default:
      return getApiErrorMessage(e, t('load_failed', 'The file couldn’t be opened right now. Try again.'));
  }
}

/**
 * Why a file could not be SENT. The server's own words win when it answered.
 *
 * ⚠️ No answer at all is the case that matters (2026-11-03, "File could not be
 * sent" for a 144 KB deck): an ad blocker stopped the upload inside the browser,
 * so nothing reached KaziPeople and the old toast gave no clue. A timeout is
 * different — the upload may have landed after we stopped waiting, so it must
 * never read as "failed, send again".
 */
export function uploadErrorMessage(e: unknown, online: boolean = typeof navigator === 'undefined' || navigator.onLine): string {
  const t = (k: string, d: string) => i18n.t(`conversations:file.${k}`, { defaultValue: d }) as string;
  const err = e as HttpLikeError;
  if (err?.response?.status) return getApiErrorMessage(e, t('send_failed', 'File could not be sent'));
  if (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT') {
    return t('upload_timeout', 'The upload took too long and was stopped. Check the conversation before sending again — it may already have arrived.');
  }
  // No response at all (offline, CORS, a dropped connection, an extension): the
  // upload may have been accepted before the answer was lost, and a new send gets
  // a fresh clientMessageId — so never "send again", only "check first".
  if (!online) return t('upload_offline', 'You’re offline, so we couldn’t confirm the file arrived — it may already have arrived. When you’re back online, check the conversation before you try again.');
  return t('upload_blocked', 'We got no answer from KaziPeople, so the file may already have arrived. Check the conversation before you try again. If you use an ad blocker or privacy extension, allow KaziPeople in it.');
}

// ── Office preview (spec §3.4) ─────────────────────────────────────────────

/** How long View waits for the server to prepare the PDF before giving up. */
export const OFFICE_PREVIEW_MAX_WAIT_MS = 60_000;
const DEFAULT_RETRY_MS = 2_000;

/** Why there is no preview: the server's 422 reasons, plus `off` for a 404
 *  (preview switched off, or not an Office file to the server). */
export type OfficeUnavailableReason = 'macro' | 'encrypted' | 'too_large' | 'unsupported' | 'failed' | 'off';
export type OfficePreviewResult =
  | { kind: 'ready'; blob: Blob }
  | { kind: 'unavailable'; reason: OfficeUnavailableReason }
  | { kind: 'cancelled' };

/** The 422 body's reason; anything unexpected reads as plain `unsupported`. */
export function officePreviewReason(body: unknown): OfficeUnavailableReason {
  const r = (body as { reason?: string } | undefined)?.reason;
  return r === 'macro' || r === 'encrypted' || r === 'too_large' || r === 'failed' || r === 'unsupported' ? r : 'unsupported';
}

/** Plain words for "no preview", each ending with what to do. */
export function officeUnavailableMessage(reason: OfficeUnavailableReason): string {
  const t = (k: string, d: string) => i18n.t(`conversations:preview.${k}`, { defaultValue: d }) as string;
  switch (reason) {
    case 'macro':
      return t('macro', 'This file contains macros, so it can’t be previewed. Download it to open it.');
    case 'encrypted':
      return t('encrypted', 'This file is password-protected, so it can’t be previewed. Download it to open it.');
    case 'too_large':
      return t('too_large', 'This file is too large to preview. Download it to open it.');
    case 'off':
      return t('off', 'Previews aren’t available for this file here. Download it to open it.');
    default:
      return t('unsupported', 'This file can’t be previewed. Download it to open it.');
  }
}

export interface PreviewResponse { status: number; data: Blob; headers?: Record<string, unknown> }

export interface PreviewRequestOptions {
  /** Time left before the 60 s promise runs out: the request's own timeout. */
  timeoutMs: number;
  /** Aborted when the viewer closes, the card goes away, or time runs out. */
  signal: AbortSignal;
}

export interface PreviewDeps {
  /** GET …/preview as a Blob, resolving with the status (200 or 202). */
  get: (o: PreviewRequestOptions) => Promise<PreviewResponse>;
  refresh: () => Promise<boolean>;
  wait?: (ms: number) => Promise<void>;
  now?: () => number;
  /** Called while the server is preparing (to show "Preparing preview…"). */
  onPreparing?: () => void;
  /** True once the viewer was closed: stop polling. */
  cancelled?: () => boolean;
  /** The caller's abort (viewer closed, card unmounted): cancels the view. */
  signal?: AbortSignal;
  /** Timers that bound renewal and the retry pause (injectable for tests). */
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

const previewTimeout = () =>
  Object.assign(new Error('The preview took too long to prepare.'), { code: 'preview_timeout' });
const isTimeoutError = (e: unknown) => {
  const code = (e as { code?: string })?.code;
  return code === 'preview_timeout' || code === 'ECONNABORTED' || code === 'ETIMEDOUT';
};

const retryAfterMs = (h?: Record<string, unknown>): number => {
  const raw = Number(h?.['retry-after'] ?? h?.['Retry-After']);
  if (!Number.isFinite(raw)) return DEFAULT_RETRY_MS;
  return Math.min(10, Math.max(1, raw)) * 1000;
};

/**
 * View an Office file: ask for its PDF, and while the server prepares it (202),
 * poll as its Retry-After says. A 422 or 404 means no preview (with a reason);
 * 410, 5xx and a lost connection are thrown for attachmentErrorMessage.
 *
 * ⚠️ ONE 60 s DEADLINE OVER EVERYTHING (round 2): requests, session renewal,
 * the network retry and the waits all count. Each request is given only the
 * time left as its timeout and an abort signal; an answer that lands after the
 * deadline — even a 200 — is `preview_timeout`, never a late PDF.
 * Closing the viewer (`cancelled`, or the caller's `signal`) aborts the request
 * in flight and stops any renewal or retry: no further request is made.
 */
export async function fetchOfficePreview(deps: PreviewDeps): Promise<OfficePreviewResult> {
  const wait = deps.wait ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const now = deps.now ?? (() => Date.now());
  const deadline = now() + OFFICE_PREVIEW_MAX_WAIT_MS;
  const ctl = new AbortController();
  const onAbort = () => ctl.abort();
  deps.signal?.addEventListener('abort', onAbort);
  const cancelled = () => !!deps.cancelled?.() || !!deps.signal?.aborted;
  const CANCELLED = new Error('cancelled');
  // Checked before any renewal or retry inside fetchAttachmentBlob.
  const stop = (e: unknown): Error | null => {
    if (cancelled()) { ctl.abort(); return CANCELLED; }
    if (now() >= deadline || isTimeoutError(e)) { ctl.abort(); return previewTimeout(); }
    return null;
  };
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((t: unknown) => clearTimeout(t as ReturnType<typeof setTimeout>));
  // Follow-up (r2): a session renewal or the network-retry pause counts against
  // the same deadline. The preview stops WAITING at the deadline (or on close);
  // the shared renewal itself isn't cancelled, other callers may need it.
  const bounded = <T,>(work: () => Promise<T>): Promise<T> => {
    if (cancelled()) return Promise.reject(CANCELLED);
    const left = deadline - now();
    if (left <= 0) return Promise.reject(previewTimeout());
    return new Promise<T>((resolve, reject) => {
      let done = false;
      const finish = (fn: () => void) => {
        if (done) return;
        done = true;
        clearTimer(timer);
        deps.signal?.removeEventListener('abort', onAbort);
        fn();
      };
      const timer = setTimer(() => finish(() => reject(previewTimeout())), left);
      const onAbort = () => finish(() => reject(CANCELLED));
      deps.signal?.addEventListener('abort', onAbort);
      work().then((v) => finish(() => resolve(v)), (e) => finish(() => reject(e)));
    });
  };
  try {
    for (;;) {
      if (cancelled()) { ctl.abort(); return { kind: 'cancelled' }; }
      if (now() >= deadline) throw previewTimeout();
      let res: PreviewResponse;
      try {
        res = await fetchAttachmentBlob<PreviewResponse>({
          // Rechecked before EVERY request, including the one after a renewal or
          // the retry pause: once the budget is spent, no request starts.
          get: () => {
            if (cancelled()) return Promise.reject(CANCELLED);
            const left = deadline - now();
            if (left <= 0) return Promise.reject(previewTimeout());
            return deps.get({ timeoutMs: left, signal: ctl.signal });
          },
          refresh: () => bounded(() => deps.refresh()),
          wait: (ms) => bounded(() => wait(ms)),
          stop,
        });
      } catch (e) {
        if (e === CANCELLED || cancelled()) { ctl.abort(); return { kind: 'cancelled' }; }
        if (isTimeoutError(e)) throw previewTimeout();
        const status = (e as HttpLikeError)?.response?.status;
        if (status === 422) return { kind: 'unavailable', reason: officePreviewReason((e as HttpLikeError).response?.data) };
        if (status === 404) return { kind: 'unavailable', reason: 'off' };
        if (!status && now() >= deadline) throw previewTimeout();
        throw e;
      }
      if (cancelled()) { ctl.abort(); return { kind: 'cancelled' }; }
      if (now() > deadline) throw previewTimeout(); // a late answer, even a 200, breaks the promise
      if (res.status !== 202) return { kind: 'ready', blob: res.data };
      deps.onPreparing?.();
      const delay = retryAfterMs(res.headers);
      if (now() + delay > deadline) throw previewTimeout();
      await wait(delay);
    }
  } finally {
    deps.signal?.removeEventListener('abort', onAbort);
  }
}

/** A blob error body (responseType 'blob') hides the server's JSON — unwrap it so
 *  getApiErrorMessage can read `{ message }`. Best-effort; never throws. */
async function unwrapBlobError(e: unknown): Promise<unknown> {
  const res = (e as HttpLikeError)?.response;
  const data = res?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      res!.data = JSON.parse(await readBlobText(data));
    } catch {
      /* not JSON — leave as is */
    }
  }
  return e;
}

function readBlobText(b: Blob): Promise<string> {
  if (typeof b.text === 'function') return b.text();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(r.error);
    r.readAsText(b);
  });
}

export interface FetchDeps<T = Blob> {
  /** GET the file as a Blob (throws axios-style errors). */
  get: () => Promise<T>;
  /** Renew the session cookie; true when it worked. */
  refresh: () => Promise<boolean>;
  /** Pause before the single network retry (injectable for tests). */
  wait?: (ms: number) => Promise<void>;
  /** Asked before a renewal or a retry, with the failure: an Error to throw
   *  instead (cancelled, out of time), or null to go on. */
  stop?: (e: unknown) => Error | null;
}

/**
 * Fetch an attachment's bytes. 401 → renew the session once and retry once;
 * network failure → retry once. Never loops. Throws the (unwrapped) error.
 */
export async function fetchAttachmentBlob<T = Blob>(deps: FetchDeps<T>): Promise<T> {
  const wait = deps.wait ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let renewed = false;
  let retriedNetwork = false;
  // At most three attempts: original, +1 after renewal, +1 after a network blip.
  for (;;) {
    try {
      return await deps.get();
    } catch (e) {
      const status = (e as HttpLikeError)?.response?.status;
      const halt = deps.stop?.(e);
      if (halt) throw halt;
      if (status === 401 && !renewed) {
        renewed = true;
        if (await deps.refresh()) continue;
      } else if (!status && !retriedNetwork) {
        retriedNetwork = true;
        await wait(800);
        continue;
      }
      throw await unwrapBlobError(e);
    }
  }
}

/** Save a blob under the ORIGINAL filename (never the URL's last segment). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'file';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
